import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <>
        <SEO title="漫画未找到 - 奇妙漫画" description="抱歉，您要找的漫画不存在或已被移除。" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-6xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已被移除。</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = getRelatedComics(comic, 6);
  const categorySlug = comic.category;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 奇妙漫画免费在线阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate,
    dateModified: comic.updateDate,
  };

  return (
    <>
      <SEO
        title={`${comic.title} - 奇妙漫画免费在线阅读 | ${comic.subcategory}漫画`}
        description={comic.description}
        canonicalUrl={`https://www.qimiaomanhua.com/comic/${comic.id}`}
        ogType="article"
        schema={pageSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t(`nav.${categorySlug}`) || categorySlug, href: `/category/${categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>

          {/* Comic Info */}
          <article className="flex flex-col md:flex-row gap-8 py-8">
            <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px] mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 奇妙漫画免费在线阅读`}
                  title={`${comic.title} | 奇妙漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{comic.title}</h1>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-foreground-500 whitespace-nowrap">{t('comic.author')}:</span>
                  <span className="text-foreground-800 font-medium">{comic.author}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-foreground-500 whitespace-nowrap">{t('comic.status')}:</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-foreground-500 whitespace-nowrap">{t('comic.category')}:</span>
                  <Link to={`/category/${comic.category}`} className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                    {comic.subcategory}
                  </Link>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-foreground-500 whitespace-nowrap">{t('comic.totalChapters')}:</span>
                  <span className="text-foreground-800 font-medium">{comic.totalChapters}话</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-foreground-500 whitespace-nowrap">{t('comic.rating')}:</span>
                  <span className="flex items-center gap-1 text-accent-500 font-bold">
                    <i className="ri-star-fill text-sm"></i> {comic.rating}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-foreground-500 whitespace-nowrap">{t('comic.views')}:</span>
                  <span className="text-foreground-800 font-medium">
                    {comic.views >= 10000000 ? `${(comic.views / 10000000).toFixed(1)}千万` : `${(comic.views / 10000).toFixed(1)}万`}
                  </span>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{t('comic.description')}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/comic/${comic.id}`}
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('comic.startReading')}
                </Link>
                <Link
                  to={`/comic/${comic.id}`}
                  className="px-6 py-2.5 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('comic.latestChapter')}: {comic.latestChapter}
                </Link>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="py-8 border-t border-background-200" aria-labelledby="chapters-heading">
            <h2 id="chapters-heading" className="font-heading text-xl font-bold text-foreground-900 mb-6">
              {t('comic.chapterList')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {comic.chapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate flex-1 mr-2">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateDate}</span>
                </Link>
              ))}
            </div>
          </section>

          {/* Related Comics */}
          <section className="py-8 border-t border-background-200 pb-14" aria-labelledby="related-heading">
            <h2 id="related-heading" className="font-heading text-xl font-bold text-foreground-900 mb-6">
              {t('comic.relatedComics')}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {related.map((rc) => (
                <ComicCard key={rc.id} comic={rc} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}